import React, { useMemo } from "react";
import { getPerformanceBadgeClass } from "../dpatScoreStats";
import DpatExportButtons from "./DpatExportButtons";
import {
  buildDistrictRankingHtml,
  buildDistrictRankingRows,
  downloadCsvFromRows,
  downloadExcelSheets,
  downloadNodeAsPdf,
  downloadWordFromHtml,
} from "../dpatDashboardExport";

function formatPillarScore(actual, max) {
  const a = Number(actual);
  const m = Number(max);
  if (!Number.isFinite(a) || !Number.isFinite(m) || m <= 0) return "—";
  return (
    <>
      {a}
      <span className="text-muted-foreground"> / {m}</span>
      <span className="ml-1 text-xs text-muted-foreground">({((a / m) * 100).toFixed(1)}%)</span>
    </>
  );
}

const DpatRankingTable = ({
  rows,
  loading,
  reportingYear,
  regionFilter,
  onRegionFilterChange,
  dataFilter,
  onDataFilterChange,
  search,
  onSearchChange,
  regionOptions,
  dataOptions,
  showRank = true,
  showRegion = true,
  title = "District DPAT rankings",
  subtitle,
  tableCaptureRef,
  scoreSource = "final",
  onScoreSourceChange,
}) => {
  const scoreLabel =
    scoreSource === "system"
      ? "System Score"
      : scoreSource === "technical"
        ? "Technical Team Score"
        : "Final Outcome";
  const sortedRows = useMemo(() => {
    return [...rows].sort((a, b) => {
      const ra = (a.rank ?? 9999) - (b.rank ?? 9999);
      if (ra !== 0) return ra;
      return a.districtName.localeCompare(b.districtName);
    });
  }, [rows]);

  const assessedInView = rows.filter((r) => r.hasData).length;
  const colSpan = (showRank ? 1 : 0) + (showRegion ? 1 : 0) + 7;

  const handleTablePdf = async () => {
    if (!tableCaptureRef?.current) return;
    await downloadNodeAsPdf(
      tableCaptureRef.current,
      `dpat-ranking-table-${reportingYear}.pdf`,
    );
  };

  const handleTableWord = () => {
    downloadWordFromHtml(
      buildDistrictRankingHtml(sortedRows, {
        title,
        year: reportingYear,
        showRegion,
        scoreLabel,
      }),
      `dpat-ranking-table-${reportingYear}.doc`,
    );
  };

  const handleTableExcel = () => {
    downloadExcelSheets(
      [{ name: "District ranking", rows: buildDistrictRankingRows(sortedRows, { showRegion, scoreLabel }) }],
      `dpat-ranking-table-${reportingYear}.xlsx`,
    );
  };

  const handleTableCsv = () => {
    downloadCsvFromRows(
      buildDistrictRankingRows(sortedRows, { showRegion, scoreLabel }),
      `dpat-ranking-table-${reportingYear}.csv`,
    );
  };

  const sourceBtnClass = (active) =>
    `rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
      active
        ? "bg-[#16325a] text-white shadow-sm"
        : "bg-transparent text-muted-foreground hover:bg-[#16325a]/10 hover:text-[#16325a]"
    }`;

  return (
    <section ref={tableCaptureRef} className="rounded-xl border border-border bg-card shadow-sm">
      <div className="border-b border-border px-4 py-4 md:px-6">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-foreground md:text-xl">{title}</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {subtitle ||
                `${reportingYear} — ${rows.length} district(s) · ${assessedInView} with ${scoreLabel}`}
            </p>
          </div>
          <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
            {onScoreSourceChange ? (
              <div data-export-hide className="flex rounded-lg border border-border bg-muted/40 p-0.5">
                <button
                  type="button"
                  className={sourceBtnClass(scoreSource === "final")}
                  onClick={() => onScoreSourceChange("final")}
                >
                  Final Outcome
                </button>
                <button
                  type="button"
                  className={sourceBtnClass(scoreSource === "technical")}
                  onClick={() => onScoreSourceChange("technical")}
                >
                  Technical Team Score
                </button>
                <button
                  type="button"
                  className={sourceBtnClass(scoreSource === "system")}
                  onClick={() => onScoreSourceChange("system")}
                >
                  System Score
                </button>
              </div>
            ) : null}
            <DpatExportButtons
              label="Download table"
              onPdf={handleTablePdf}
              onWord={handleTableWord}
              onExcel={handleTableExcel}
              onCsv={handleTableCsv}
              disabled={loading || sortedRows.length === 0}
            />
          </div>
        </div>
        <div data-export-hide className="mt-4 flex w-full flex-col gap-3 lg:max-w-3xl lg:ml-auto">
            <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Region
              </label>
              <select
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                value={regionFilter}
                onChange={(e) => onRegionFilterChange(e.target.value)}
              >
                {regionOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Data
              </label>
              <select
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                value={dataFilter}
                onChange={(e) => onDataFilterChange(e.target.value)}
              >
                {dataOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2 lg:col-span-1">
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Search
              </label>
              <input
                type="search"
                placeholder="District or region…"
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
              />
            </div>
            </div>
          </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[860px] text-left text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              {showRank && <th className="px-4 py-3 font-semibold md:px-6">Rank</th>}
              <th className="px-4 py-3 font-semibold md:px-6">District</th>
              {showRegion && <th className="px-4 py-3 font-semibold md:px-6">Region</th>}
              <th className="px-4 py-3 font-semibold md:px-6">{scoreLabel}</th>
              <th className="px-4 py-3 font-semibold md:px-6">%</th>
              <th className="px-4 py-3 font-semibold md:px-6">SDI</th>
              <th className="px-4 py-3 font-semibold md:px-6">PI</th>
              <th className="px-4 py-3 font-semibold md:px-6">Classification</th>
              <th className="px-4 py-3 font-semibold md:px-6">Compliance</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={colSpan} className="px-4 py-8 text-center text-muted-foreground md:px-6">
                  Loading rankings…
                </td>
              </tr>
            ) : sortedRows.length === 0 ? (
              <tr>
                <td colSpan={colSpan} className="px-4 py-8 text-center text-muted-foreground md:px-6">
                  No districts match the current filters.
                </td>
              </tr>
            ) : (
              sortedRows.map((row) => (
                <tr key={row.districtId} className="border-b border-border/60 hover:bg-muted/20">
                  {showRank && (
                    <td className="px-4 py-3 font-bold tabular-nums text-foreground md:px-6">
                      {row.rank != null ? row.rank : "—"}
                    </td>
                  )}
                  <td className="px-4 py-3 font-medium text-foreground md:px-6">{row.districtName}</td>
                  {showRegion && (
                    <td className="px-4 py-3 text-muted-foreground md:px-6">{row.regionName}</td>
                  )}
                  <td className="px-4 py-3 tabular-nums md:px-6">
                    {row.hasData ? (
                      <>
                        {row.overallScore}
                        <span className="text-muted-foreground"> / {row.overallMax}</span>
                      </>
                    ) : (
                      <span className="text-muted-foreground">No data</span>
                    )}
                  </td>
                  <td className="px-4 py-3 font-semibold tabular-nums md:px-6">
                    {row.overallPercent != null ? `${row.overallPercent.toFixed(1)}%` : "—"}
                  </td>
                  <td className="px-4 py-3 tabular-nums md:px-6">
                    {row.hasData ? formatPillarScore(row.sdiActual, row.sdiMax) : "—"}
                  </td>
                  <td className="px-4 py-3 tabular-nums md:px-6">
                    {row.hasData ? formatPillarScore(row.piActual, row.piMax) : "—"}
                  </td>
                  <td className="px-4 py-3 md:px-6">
                    {row.classification ? (
                      <span
                        className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium ${getPerformanceBadgeClass(row.classification)}`}
                      >
                        {row.classification}
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-4 py-3 tabular-nums text-muted-foreground md:px-6">
                    {row.hasData && row.complianceTotal > 0
                      ? `${row.complianceFulfilled} / ${row.complianceTotal}`
                      : "—"}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default DpatRankingTable;
